import config from '@/config';
import resolveFirstModelRoute from '@/helpers/routing';
import i18n from '@/plugins/i18n';

const collectionPatterns = Object.values(config.app.collections)
  .map((c) => c.slug)
  .join('|');

export default [
  {
    name: 'dashboard',
    path: '',
    component: () => import('@/views/Dashboard.vue'),
  },
  {
    name: 'collection',
    path: `/:collection(${collectionPatterns})`,
    component: () => import('@/views/Categories.vue'),
    props: (route) => ({
      collection: route.params.collection,
    }),
    beforeEnter: (to) => (
      to.params.collection === config.app.collections.basics.slug ? config.basics_enabled : true
    ),
  },
  {
    name: 'collection-search',
    path: `/:collection(${collectionPatterns})/search/:search`,
    component: () => import('@/views/Products.vue'),
    props: (route) => ({
      collection: route.params?.collection,
      query: route.params?.search,
    }),
    beforeEnter: (to) => (
      to.params?.collection === config.app.collections.basics.slug ? config.basics_enabled : true
    ),
  },
  {
    name: 'products',
    path: `/:collection(${collectionPatterns})/categories/:category`,
    component: () => import('@/views/Products.vue'),
    props: (route) => ({
      collection: route.params.collection,
      categoryId: +route.params.category,
    }),
    beforeEnter: (to) => {
      if (!Number.isFinite(+to.params.category)) {
        return { name: 'dashboard' };
      }

      return to.params.collection === config.app.collections.basics.slug
        ? config.basics_enabled
        : true;
    },
  },
  {
    name: 'product',
    path: `/:collection(${collectionPatterns}|brigante)/categories/:category/product/:product`,
    component: () => import('@/views/Product.vue'),
    beforeEnter: (to, from, next) => {
      if (
        to.params.collection === 'brigante'
        && config.app.collections.brigante.slug !== 'brigante'
      ) {
        next({
          ...to,
          params: {
            ...to.params,
            collection: config.app.collections.brigante.slug,
          },
        });
      } else if (
        to.params.collection !== config.app.collections.basics.slug
        || config.basics_enabled
      ) {
        next();
      }
    },
    props: (route) => ({
      id: +route.params.product,
      collection: route.params.collection,
      variantFilters: (product) => [
        ['product_tmpl_id', '=', product.product_tmpl_id[0]],
      ],
      orderRoute: (variant) => ({
        name: 'category_order',
        params: {
          variant,
          category: route.params.collection === config.app.collections.basics.slug ? 'basics' : 'osa',
        },
      }),
    }),
  },
  {
    name: 'slipper-product',
    path: '/slippers/categories/:category/product/:product',
    component: () => import('@/views/Product.vue'),
    props: (route) => ({
      id: +route.params.product,
      collection: 'slippers',
      variantFilters: (product) => [
        ['categ_id', '=', product.categ_id[0]],
      ],
      orderRoute: (variant) => ({
        name: 'slipper',
        params: {
          product: variant,
        },
      }),
    }),
  },
  {
    name: 'search',
    path: '/search/:search',
    component: () => import('@/views/Products.vue'),
    props: (route) => ({
      query: route.params?.search,
    }),
  },
  {
    name: 'category_order',
    meta: {
      auth: true,
    },
    path: '/order/:category(osa|basics)/:variant/:orderId?',
    props: (route) => ({
      category: route.params.category,
      variant: +route.params.variant,
      orderId: +route.params.orderId,
    }),
    component: () => import('@/views/ShoeProductOrder.vue'),
  },
  {
    name: 'order',
    path: '/order/:variant/:orderId?',
    redirect: (to) => ({
      name: 'category_order',
      params: {
        category: 'osa',
        variant: to.params.variant,
      },
    }),
  },
  {
    name: 'custom-order',
    meta: {
      auth: true,
    },
    path: '/custom-order/:orderId?',
    props: (route) => ({
      orderId: +route.params.orderId,
    }),
    component: () => import('@/views/ShoeOrder.vue'),
  },
  {
    name: 'reorder',
    meta: {
      auth: true,
    },
    path: '/reorder/:variant/:orderId',
    props: (route) => ({
      variant: +route.params.variant,
      orderId: +route.params.orderId,
      reorder: true,
    }),
    component: () => import('@/views/ShoeProductOrder.vue'),
  },
  {
    name: 'custom-reorder',
    meta: {
      auth: true,
    },
    path: '/custom-reorder/:orderId',
    props: (route) => ({
      orderId: +route.params.orderId,
      reorder: true,
    }),
    component: () => import('@/views/ShoeOrder.vue'),
  },
  {
    name: 'osb-overview',
    meta: {
      auth: true,
    },
    path: '/osb',
    component: () => import('@/views/osb/Overview.vue'),
  },
  {
    name: 'lasts-supplements-foils-overview',
    meta: {
      auth: true,
    },
    path: '/lasts-supplements-foils',
    component: () => import('@/views/lsf/Overview.vue'),
  },
  {
    name: 'lasts-supplements-foils',
    meta: {
      auth: true,
    },
    path: '/lasts-supplements-foils/order',
    component: () => import('@/views/lsf/Order.vue'),
  },
  {
    name: 'finishing',
    meta: {
      auth: true,
    },
    path: '/finishing',
    component: () => import('@/views/Finishing.vue'),
    beforeEnter: () => config.finishing_enabled,
  },
  {
    name: 'flea-shoes',
    meta: {
      auth: true,
    },
    path: '/flea',
    component: () => import('@/views/FleaProducts.vue'),
    beforeEnter: () => config.flea_enabled,
  },
  {
    name: 'flea',
    meta: {
      auth: true,
    },
    path: '/flea/:product',
    props: (route) => ({
      variant: +route.params.product,
    }),
    component: () => import('@/views/Flea.vue'),
    beforeEnter: () => config.flea_enabled,
  },
  {
    name: 'leg-cover',
    meta: {
      auth: true,
    },
    path: '/leg-cover',
    component: () => import('@/views/LegCover.vue'),
  },
  {
    name: 'trial-shoe',
    meta: {
      auth: true,
    },
    path: '/trial-shoe',
    component: () => import('@/views/TrialShoe.vue'),
  },
  {
    name: 'liner-shoe',
    meta: {
      auth: true,
    },
    path: '/liner-shoe',
    component: () => import('@/views/LinerShoe.vue'),
  },
  {
    name: 'bath-shoes',
    meta: {
      auth: true,
    },
    props: () => ({
      title: i18n.global.t('order.bath_shoe'),
      route: 'bath-shoe',
      handle: 'Badschoen',
    }),
    path: '/bath-shoes',
    component: () => import('@/views/Models.vue'),
  },
  {
    name: 'bath-shoe',
    meta: {
      auth: true,
    },
    props: (route) => ({
      variant: +route.params.product,
    }),
    path: '/bath-shoe/:product',
    component: () => import('@/views/BathShoe.vue'),
  },
  {
    name: 'slippers',
    meta: {
      auth: true,
    },
    path: '/slippers',
    beforeEnter: () => resolveFirstModelRoute('Pantoffel', 'slipper-product'),
  },
  {
    name: 'slipper',
    meta: {
      auth: true,
    },
    path: '/slippers/:product',
    props: (route) => ({
      variant: +route.params.product,
    }),
    component: () => import('@/views/Slipper.vue'),
  },
  {
    name: 'cart',
    meta: {
      auth: true,
    },
    path: '/cart',
    component: () => import('@/views/Cart.vue'),
  },
  {
    name: 'order-history',
    meta: {
      auth: true,
    },
    path: '/order-history',
    component: () => import('@/views/OrderHistory.vue'),
  },
  {
    name: 'order-repair-request',
    meta: {
      auth: true,
    },
    path: '/order-repair-request/:oldOrderId/:newOrderId',
    props: (route) => ({
      restoreId: +route.params.oldOrderId,
      repairId: +route.params.newOrderId,
    }),
    component: () => import('@/views/OrderHistory.vue'),
  },
  {
    name: 'order-restore',
    meta: {
      auth: true,
    },
    path: '/order-history/restore/:orderId',
    props: (route) => ({
      restoreId: +route.params.orderId,
    }),
    component: () => import('@/views/OrderHistory.vue'),
  },
];
